import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import API from "./config/API";

function UserPaymentForm() {
  const navigate = useNavigate();
  const location = useLocation();

  const userData = location.state || {};

  // GET userBookingId FROM PREVIOUS PAGE
  const userBookingId = userData.userBookingId || "";

  const userFullName =
    `${userData.firstName || ""} ${userData.lastName || ""}`.trim();

  const totalBedCost = userData.totalBedCost || userData.bedCost || 0;

  const [showPopup, setShowPopup] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const loggedInUser = JSON.parse(localStorage.getItem("user") || "{}");

  const [form, setForm] = useState({
    userFullName,
    totalBedAmount: totalBedCost,
    userPaidAmount: "",
    isAnyPendingAmount: false,
    paymentUpdatedBy: loggedInUser?.emailId || "",
    paymentType: "",
  });

  // ================= AUTO PENDING =================
  useEffect(() => {
    const total = Number(form.totalBedAmount || 0);
    const paid = Number(form.userPaidAmount || 0);

    setForm((prev) => ({
      ...prev,
      isAnyPendingAmount: paid < total,
    }));
  }, [form.userPaidAmount, form.totalBedAmount]);

  // ================= HANDLE CHANGE =================
  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  // ================= PENDING AMOUNT =================
  const pendingAmount =
    Number(form.totalBedAmount || 0) - Number(form.userPaidAmount || 0);

  // ================= VALIDATION =================
  const validate = () => {
    let tempErrors = {};

    const total = Number(form.totalBedAmount || 0);
    const paid = Number(form.userPaidAmount || 0);

    if (!form.userPaidAmount || paid <= 0) {
      tempErrors.userPaidAmount = "Paid amount is required";
    }

    if (paid > total) {
      tempErrors.userPaidAmount = "Paid amount cannot be greater than total";
    }

    if (!form.paymentType) {
      tempErrors.paymentType = "Please select payment type";
    }

    setErrors(tempErrors);

    return Object.keys(tempErrors).length === 0;
  };

  // ================= SUBMIT PAYMENT =================
  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);

    // Read logged-in user
    const user = JSON.parse(localStorage.getItem("user") || "{}");

    // JWT Token
    const token = user?.jwtToken;

    // Dynamic API URL
    const paymentUrl = `${API.V1}/user/userPayment`;

    const payload = {
      userBookingId: userBookingId,
      firstName: form.userFullName,
      totalBedAmount: Number(form.totalBedAmount),
      userPaidAmount: Number(form.userPaidAmount),
      isAnyPendingAmount: pendingAmount > 0,
      paymentUpdatedBy: form.paymentUpdatedBy,
      paymentType: form.paymentType,
    };

    console.log("Payment API URL:", paymentUrl);
    console.log("Payment Payload:", payload);

    try {
      const response = await fetch(paymentUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      // ================= TOKEN EXPIRED =================
      if (response.status === 401) {
        alert("Session Expired. Please login again.");

        localStorage.removeItem("user");

        navigate("/login");
        return;
      }

      const data = await response.json();

      console.log("Payment Response:", data);

      // ================= SUCCESS =================
      if (data.statusCode === 200) {
        setShowPopup(true);

        setTimeout(() => {
          setShowPopup(false);
          navigate("/dashboard");
        }, 2000);
      } else {
        alert(data.message || "Payment Failed");
      }
    } catch (error) {
      console.error("Payment API Error:", error);
      alert("Server Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      {/* SUCCESS POPUP */}
      {showPopup && <div style={styles.popup}>✅ Payment Successful</div>}

      <div style={styles.card}>
        <h1 style={styles.title}>💳 User Payment Form</h1>

        {/* ================= FORM GRID ================= */}
        <div style={styles.grid}>
          {/* USER FULL NAME */}
          <div style={styles.fieldBoxBlue}>
            <label style={styles.label}>User Full Name</label>

            <input value={form.userFullName} readOnly style={styles.input} />
          </div>

          {/* TOTAL BED COST */}
          <div style={styles.fieldBoxOrange}>
            <label style={styles.label}>Total Bed Cost</label>

            <input value={form.totalBedAmount} readOnly style={styles.input} />
          </div>

          {/* PAID AMOUNT */}
          <div style={styles.fieldBoxGreen}>
            <label style={styles.label}>Paid Amount</label>

            <input
              type="number"
              name="userPaidAmount"
              value={form.userPaidAmount}
              onChange={handleChange}
              placeholder="Enter paid amount"
              min="1"
              style={styles.input}
            />

            {errors.userPaidAmount && (
              <p style={styles.error}>{errors.userPaidAmount}</p>
            )}
          </div>

          {/* PAYMENT TYPE */}
          <div style={styles.fieldBoxPurple}>
            <label style={styles.label}>Payment Type</label>

            <select
              name="paymentType"
              value={form.paymentType}
              onChange={handleChange}
              style={styles.input}
            >
              <option value="">Select</option>
              <option value="CASH">CASH</option>
              <option value="UPI">UPI</option>
              <option value="CARD">CARD</option>
              <option value="BANK_TRANSFER">BANK_TRANSFER</option>
            </select>

            {errors.paymentType && (
              <p style={styles.error}>{errors.paymentType}</p>
            )}
          </div>
        </div>

        {/* ================= SUMMARY ================= */}
        <div style={styles.summaryBox}>
          <div style={styles.summaryCard}>
            💰 Paid: ₹ {form.userPaidAmount || 0}
          </div>

          <div style={styles.summaryCard}>
            🛏 Total: ₹ {form.totalBedAmount || 0}
          </div>

          <div style={styles.summaryCard}>
            ⚠ Pending: ₹ {pendingAmount > 0 ? pendingAmount : 0}
          </div>

          <div style={styles.summaryCard}>
            {form.paymentType || "Payment Type"}
          </div>
        </div>

        {/* ================= SUBMIT BUTTON ================= */}
        <button
          onClick={handleSubmit}
          disabled={loading}
          style={{
            ...styles.button,
            opacity: loading ? 0.7 : 1,
            cursor: loading ? "not-allowed" : "pointer",
          }}
        >
          {loading ? "Processing..." : "Submit Payment"}
        </button>
      </div>
    </div>
  );
}

export default UserPaymentForm;

/* ================= STYLES ================= */

const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "linear-gradient(135deg,#dbeafe,#eff6ff,#f0f9ff)",
    padding: "30px",
    fontFamily: "Segoe UI",
  },

  popup: {
    position: "fixed",
    top: "20px",
    right: "20px",
    background: "#16a34a",
    color: "#fff",
    padding: "15px 25px",
    borderRadius: "12px",
    fontWeight: "700",
    zIndex: 9999,
    boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
  },

  card: {
    width: "100%",
    maxWidth: "1100px",
    background: "#fff",
    borderRadius: "24px",
    padding: "40px",
    boxShadow: "0 15px 40px rgba(0,0,0,0.12)",
  },

  title: {
    textAlign: "center",
    fontSize: "38px",
    fontWeight: "800",
    marginBottom: "30px",
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(250px,1fr))",
    gap: "20px",
  },

  fieldBoxBlue: {
    background: "#dbeafe",
    padding: "16px",
    borderRadius: "16px",
  },

  fieldBoxGreen: {
    background: "#dcfce7",
    padding: "16px",
    borderRadius: "16px",
  },

  fieldBoxOrange: {
    background: "#ffedd5",
    padding: "16px",
    borderRadius: "16px",
  },

  fieldBoxPurple: {
    background: "#ede9fe",
    padding: "16px",
    borderRadius: "16px",
  },

  label: {
    fontWeight: "700",
    marginBottom: "8px",
    display: "block",
  },

  input: {
    width: "100%",
    padding: "12px",
    borderRadius: "10px",
    border: "1px solid #ccc",
    boxSizing: "border-box",
    fontSize: "15px",
  },

  error: {
    color: "red",
    fontSize: "12px",
    marginTop: "5px",
    marginBottom: 0,
  },

  summaryBox: {
    marginTop: "30px",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))",
    gap: "15px",
  },

  summaryCard: {
    background: "#2563eb",
    color: "#fff",
    padding: "18px",
    borderRadius: "14px",
    textAlign: "center",
    fontWeight: "700",
  },

  button: {
    marginTop: "30px",
    width: "100%",
    padding: "16px",
    background: "#4f46e5",
    color: "#fff",
    fontSize: "18px",
    fontWeight: "700",
    border: "none",
    borderRadius: "14px",
  },
};
